'use client';

import { c as _c } from "react/compiler-runtime";
import { spaceTimeRequirementCopy } from '@shared/all/domains/factiiiRequirementOptions';
import { type FactiiiRequirement } from '@shared/all/domains/factiiis';
import type { AddFactiiiAction, AddFactiiiState } from '@shared/utils/helpers/addFactiii/formState';
import { additionalOptionFor, EXTRA_ORDER, POST_ORDER, type SectionKey, SOURCE_ORDER, VALUE_ORDER, valueOptionFor } from '@shared/utils/helpers/addFactiii/sections';
import type { Dispatch, ReactNode } from 'react';
import { LUCIDE } from '../../../utils/lucideIcons';
import BotSettings from './BotSettings';
import GroupPicker from './GroupPicker';
import { ChoiceRow, t } from './parts';
import RulesEditor from './RulesEditor';
import SpacePicker from './SpacePicker';
type BodyProps = {
  state: AddFactiiiState;
  dispatch: Dispatch<AddFactiiiAction>;
};
const ValueBody = t0 => {
  const $ = _c(11);
  const {
    state,
    dispatch
  } = t0;
  let t1;
  if ($[0] !== dispatch || $[1] !== state) {
    t1 = requirement => {
      const option = valueOptionFor(requirement);
      if (!option) {
        return null;
      }
      return <ChoiceRow key={requirement ?? "none"} radio={true} icon={option.icon ? LUCIDE[option.icon] : null} label={option.label} description={option.description} selected={state.dataRequirement === requirement} onClick={() => dispatch({
        type: "patch",
        patch: {
          dataRequirement: requirement
        }
      })} />;
    };
    $[0] = dispatch;
    $[1] = state;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const row = t1;
  let t2;
  if ($[3] !== row) {
    t2 = VALUE_ORDER.map(row);
    $[3] = row;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className={`px-0.5 pt-2 ${t.micro}`}>Or a link to where it came from</p>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== row) {
    t4 = SOURCE_ORDER.map(row);
    $[6] = row;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <div className="space-y-1.5">{t2}{t3}{t4}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
const PlaceBody = t0 => {
  const $ = _c(12);
  const {
    state,
    dispatch
  } = t0;
  let t1;
  if ($[0] !== dispatch || $[1] !== state.locationRequirement) {
    t1 = spaceTimeRequirementCopy.map(option => <ChoiceRow key={option.value ?? "none"} radio={true} icon={option.icon ? LUCIDE[option.icon] : null} label={option.label} description={option.description} selected={state.locationRequirement === option.value} onClick={() => dispatch({
      type: "patch",
      patch: {
        locationRequirement: option.value
      }
    })} />);
    $[0] = dispatch;
    $[1] = state.locationRequirement;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== dispatch || $[4] !== state.needsTime) {
    t2 = () => dispatch({
      type: "patch",
      patch: {
        needsTime: !state.needsTime
      }
    });
    $[3] = dispatch;
    $[4] = state.needsTime;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== state.needsTime || $[7] !== t2) {
    t3 = <ChoiceRow label="Pick a date and time" description="Every tag records when it happened" selected={state.needsTime} onClick={t2} />;
    $[6] = state.needsTime;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t3) {
    t4 = <div className="space-y-1.5">{t1}{t3}</div>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
const CheckboxBody = t0 => {
  const $ = _c(9);
  const {
    state,
    dispatch,
    order
  } = t0;
  let t1;
  if ($[0] !== dispatch || $[1] !== order || $[2] !== state) {
    let t2;
    if ($[4] !== dispatch || $[5] !== state) {
      t2 = requirement => {
        const option = additionalOptionFor(requirement);
        if (!option) {
          return null;
        }
        return <ChoiceRow key={requirement} icon={option.icon ? LUCIDE[option.icon] : null} label={option.label} description={option.description} selected={state.extraRequirements.includes(requirement)} onClick={() => dispatch({
          type: "toggleExtra",
          requirement
        })} />;
      };
      $[4] = dispatch;
      $[5] = state;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = order.map(t2);
    $[0] = dispatch;
    $[1] = order;
    $[2] = state;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <div className="space-y-1.5">{t1}</div>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};

/** The web body for each shared section key. Titles, icons and summaries
 *  come from the shared registry; only the controls are drawn here. */
export const SECTION_BODIES: Record<SectionKey, (props: BodyProps) => ReactNode> = {
  space: ({
    state,
    dispatch
  }) => <SpacePicker value={state.spaceSlug} onChange={spaceSlug => dispatch({
    type: 'patch',
    patch: {
      spaceSlug
    }
  })} />,
  value: props => <ValueBody {...props} />,
  place: props => <PlaceBody {...props} />,
  post: props => <CheckboxBody {...props} order={POST_ORDER} />,
  extras: props => <CheckboxBody {...props} order={EXTRA_ORDER} />,
  group: ({
    state,
    dispatch
  }) => <GroupPicker state={state} dispatch={dispatch} />,
  bot: ({
    state,
    dispatch
  }) => <BotSettings state={state} dispatch={dispatch} />,
  rules: ({
    state,
    dispatch
  }) => <RulesEditor state={state} dispatch={dispatch} />
};